// Change password (spec 20 §3) — POST /v3/me/change-password.
//
// Web cookie caveat: the refresh cookie is scoped Path=/v3/auth/refresh, so the browser
// will NOT attach it to /v3/me/change-password — the backend cannot identify the current
// session and will revoke ALL refresh tokens (current_session_kept=false). We therefore
// implement BOTH branches of 20 §3.2 but on web the realistic path is the forced
// re-login one (step 6). Flagged to integrator/backend in the summary.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError, apiFetch, refreshAccessToken } from '../../lib/api';
import { useAuth } from '../../auth/AuthContext';
import { brand } from '../../lib/brand';
import { passwordMeetsPolicy, passwordPolicyError, passwordPolicyHint, policyFromApiError } from '../../lib/passwordPolicy';
import type { PasswordPolicy } from '../../lib/passwordPolicy';
import { PasswordInput } from '../../components/PasswordInput';

interface ChangePasswordData {
  changed: boolean;
  sessions_revoked: number;
  current_session_kept: boolean;
}

// 20 §3(APP#59):新密碼規則由有效政策動態決定;≠ old_password 為固定規則。
function validateNewPassword(pw: string, old: string, policy: PasswordPolicy, t: TFunction): string | null {
  if (!passwordMeetsPolicy(pw, policy)) return passwordPolicyError(policy, t);
  if (pw === old) return t('account.pwReused');
  return null;
}

export default function ChangePasswordCard() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  // bootstrap 有效政策起始;422 WEAK_PASSWORD 帶回的 server 政策覆寫刷新(server=最終權威)。
  const [policy, setPolicy] = useState(() => brand.passwordPolicy);

  const submit = async () => {
    setError(null);
    if (!oldPw) {
      setError(t('account.pwEnterOld'));
      return;
    }
    const v = validateNewPassword(newPw, oldPw, policy, t);
    if (v) {
      setError(v);
      return;
    }
    if (newPw !== confirmPw) {
      setError(t('account.pwMismatch'));
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<ChangePasswordData>('/v3/me/change-password', {
        method: 'POST',
        body: { old_password: oldPw, new_password: newPw },
        idempotencyKey: crypto.randomUUID(),
      });
      const { sessions_revoked, current_session_kept } = res.data;
      if (current_session_kept) {
        // 20 §3.2 steps 1–4: keep session — silent refresh so the kept refresh chain
        // issues a fresh access token; no logout UI.
        setOkMsg(t('account.pwChangedOthers', { count: sessions_revoked }));
        setOldPw('');
        setNewPw('');
        setConfirmPw('');
        try {
          await refreshAccessToken();
        } catch {
          // refresh failed → session-expired path: fall through to full logout
          await logout();
          return;
        }
      } else {
        // 20 §3.2 step 6: current session NOT kept → full local logout → login page
        setOkMsg(t('account.pwChangedAll'));
        window.setTimeout(() => {
          void logout();
        }, 3000);
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'WEAK_PASSWORD') {
        // server=最終權威:帶 policy 時以 server 值刷新提示與後續前端驗證。
        const serverPolicy = policyFromApiError(e);
        if (serverPolicy) setPolicy(serverPolicy);
        setError(passwordPolicyError(serverPolicy ?? policy, t));
      } else if (e instanceof ApiError) {
        const map: Record<string, string> = {
          OLD_PASSWORD_MISMATCH: t('account.errOldPasswordMismatch'),
          PASSWORD_REUSED: t('account.pwReused'),
          RATE_LIMITED: t('account.errRateLimited'),
          REQUEST_IN_PROGRESS: t('account.errRequestInProgress'),
          ACCOUNT_INACTIVE: t('account.errAccountInactive'),
        };
        setError(map[e.code] ?? `${e.message}（${e.code}）`);
        if (e.code === 'ACCOUNT_INACTIVE') void logout();
      } else {
        setError(t('common.submitFailedNetwork'));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div>
        <button className="btn-ghost" onClick={() => setOpen(true)}>
          {t('account.changePassword')}
        </button>
        {okMsg && (
          <p style={{ color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{okMsg}</p>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-5)', maxWidth: 380 }}
    >
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)' }}>{t('account.changePassword')}</h3>
      <PasswordInput
        placeholder={t('account.oldPasswordPlaceholder')}
        autoComplete="current-password"
        value={oldPw}
        onChange={(e) => setOldPw(e.target.value)}
        disabled={busy}
      />
      <PasswordInput
        placeholder={t('account.newPasswordPlaceholder')}
        autoComplete="new-password"
        value={newPw}
        onChange={(e) => setNewPw(e.target.value)}
        disabled={busy}
        aria-describedby="change-pw-hint"
      />
      <p id="change-pw-hint" className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        {passwordPolicyHint(policy, t)}
      </p>
      <PasswordInput
        placeholder={t('account.confirmPasswordPlaceholder')}
        autoComplete="new-password"
        value={confirmPw}
        onChange={(e) => setConfirmPw(e.target.value)}
        disabled={busy}
      />
      <p className="text-secondary" style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
        {t('account.changePasswordNote')}
      </p>
      {error && <p className="error-text" style={{ margin: 0 }}>{error}</p>}
      {okMsg && (
        <p style={{ margin: 0, color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{okMsg}</p>
      )}
      <div style={{ display: 'flex', gap: 'var(--spacing-4)' }}>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.submitting') : t('account.confirmChange')}
        </button>
        <button type="button" className="btn-ghost" disabled={busy} onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
